import React, { useContext, useMemo, useState } from "react";
import { Layout, Menu, theme, Button, Drawer } from "antd";
import type { MenuProps } from "antd";
import { Link } from "react-router-dom";
import {
  RightOutlined,
  HomeOutlined,
  LeftOutlined,
  PlusOutlined,
  ApartmentOutlined,
  PhoneOutlined,
  UserAddOutlined,
  ShopOutlined,
  FileExclamationOutlined,
  FileOutlined,
  UsergroupAddOutlined,
  ToolOutlined,
  FileAddOutlined,
  MenuOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { ROUTES } from "~/utils";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import { useMediaQuery } from "@mui/material";
import { userContext } from "~/context/ContextProvider";
import * as APITypes from "~/API";

export const SideNavigation = () => {
  const { Sider } = Layout;
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const { t } = useAppTranslation();
  const { status } = useContext(userContext);
  const smallerMenu = useMediaQuery("(max-width: 650px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const width = smallerMenu ? 190 : 230;

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key !== "toggle-key" && isMobile) {
      setIsDrawerOpen(false);
    }
    if (key !== "toggle-key" && !isMobile && smallerMenu) {
      setIsMenuCollapsed(true);
    }
  };

  const menuItems: MenuProps["items"] = useMemo(() => {
    const items: MenuProps["items"] = [];

    if (!isMobile) {
      items.push({
        key: "toggle-key",
        icon: isMenuCollapsed ? <RightOutlined /> : <LeftOutlined />,
        label: isMenuCollapsed ? t("menu_item_show") : t("menu_item_hide"),
        onClick: () => setIsMenuCollapsed(!isMenuCollapsed),
      });
    }

    items.push({
      key: "home",
      icon: <HomeOutlined />,
      label: <Link to={ROUTES.home}>{t("menu_item_home")}</Link>,
    });

    if (status === APITypes.TenantStatusType.Active) {
      items.push({
        key: "amenity",
        icon: <ShopOutlined />,
        label: <Link to={ROUTES.amenity}>{t("Amenities")}</Link>,
      });
    }

    items.push({
      key: "units",
      icon: <ApartmentOutlined />,
      label: <Link to={ROUTES.unit}>{t("Units")}</Link>,
    });

    const leaseChildren: MenuProps["items"] = [];
    if (status === APITypes.TenantStatusType.Active) {
      leaseChildren.push({
        key: "lease-renewal",
        icon: <FileAddOutlined />,
        label: <Link to={ROUTES.leaseRenewalPage}>Lease Renewal</Link>,
      });
    }
    leaseChildren.push({
      key: "lease-list",
      icon: <FileOutlined />,
      label: <Link to={ROUTES.lease}>{t("Lease_Info")}</Link>,
    });
    items.push({
      key: "lease",
      icon: <FileOutlined />,
      label: t("Lease_Info"),
      children: leaseChildren,
    });

    if (status === APITypes.TenantStatusType.Active) {
      items.push({
        key: "service-request",
        icon: <ToolOutlined />,
        label: t("Service_Requests"),
        children: [
          {
            key: "service-request-new",
            icon: <PlusOutlined />,
            label: (
              <Link to={ROUTES.serviceRequestNew}>New Service Request</Link>
            ),
          },
          {
            key: "service-request-list",
            icon: <ToolOutlined />,
            label: (
              <Link to={ROUTES.serviceRequestTablePage}>
                {t("Service_Requests")}
              </Link>
            ),
          },
        ],
      });

      items.push({
        key: "review",
        icon: <UsergroupAddOutlined />,
        label: t("Review"),
        children: [
          {
            key: "review-page",
            icon: <UserAddOutlined />,
            label: <Link to={ROUTES.reviewPage}>{t("Review")}</Link>,
          },
        ],
      });
    }

    items.push({
      key: "payments",
      icon: <FileExclamationOutlined />,
      label: <Link to={ROUTES.paymentTablePage}>{t("Payments")}</Link>,
    });

    items.push({
      key: "help",
      icon: <PhoneOutlined />,
      label: <Link to={ROUTES.help}>{t("Help")}</Link>,
    });

    return items;
  }, [isMobile, isMenuCollapsed, status, t]);

  const menuContent = (
    <Menu
      mode="inline"
      defaultSelectedKeys={["home"]}
      style={{ height: "100%", borderRight: 0 }}
      onClick={handleMenuClick}
      items={menuItems}
    />
  );

  if (isMobile) {
    return (
      <>
        <Button
          type="primary"
          icon={<MenuOutlined />}
          onClick={() => setIsDrawerOpen(true)}
          aria-label={t("menu_item_show")}
          style={{
            position: "fixed",
            left: 12,
            top: 72,
            zIndex: 1000,
            width: 40,
            height: 40,
            borderRadius: "50%",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.85,
          }}
        />
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <MenuOutlined />
              <span>{t("Menu")}</span>
            </div>
          }
          placement="left"
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}
          width="80%"
          styles={{ body: { padding: 0, overflow: "auto" } }}
          closeIcon={<CloseOutlined />}
        >
          {menuContent}
        </Drawer>
      </>
    );
  }

  return (
    <Sider
      width={width}
      collapsedWidth={50}
      style={{
        background: colorBgContainer,
        borderRight: "1px solid grey",
        padding: 0,
        marginLeft: 0,
        minHeight: 0,
        overflow: "auto",
        scrollbarWidth: "thin",
      }}
      collapsible
      collapsed={isMenuCollapsed}
      reverseArrow={false}
      trigger={null}
    >
      {menuContent}
    </Sider>
  );
};
